import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import Sidebar from './Sidebar';
import MobileBottomNav from './MobileBottomNav';
import BlockingModal from '../BlockingModal';
import FeedbackWidget from '../FeedbackWidget';
import jobAPI from '../../services/jobAPI';
import { useAuth } from '../../contexts/AuthContext';
import { isTransportationProvider } from '../../utils/providerUtils';

const AuthenticatedLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [overdueReportJobs, setOverdueReportJobs] = useState([]);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const location = useLocation();
  const { profile } = useAuth();
  const isTransport = isTransportationProvider(profile);

  useEffect(() => {
    if (isTransport) {
      setOverdueReportJobs([]);
      setShowOverdueModal(false);
      return undefined;
    }
    checkBlockingConditions();
    const interval = setInterval(checkBlockingConditions, 60000);
    return () => clearInterval(interval);
  }, [location.pathname, isTransport]);

  const checkBlockingConditions = async () => {
    try {
      const response = await jobAPI.getMyJobs({ limit: 100 });
      const jobs = response.data.data.jobs;
      const now = new Date();

      // Find overdue reports (>24 hours)
      const overdueReports = jobs.filter(job => {
        if (job.status !== 'completed' || job.completion_report_submitted) return false;
        if (!job.completed_at) return false;
        const completedTime = new Date(job.completed_at);
        const hoursSince = (now - completedTime) / (1000 * 60 * 60);
        return hoursSince > 24;
      });

      setOverdueReportJobs(overdueReports);
      setShowOverdueModal(overdueReports.length > 0);
    } catch (error) {
      console.error('Error checking blocking conditions:', error);
    }
  };

  // Only block on job search and job details pages, not on pending actions or job detail pages
  const shouldShowBlocking = () => {
    const blockablePages = ['/jobs/search', '/dashboard', '/schedule'];
    return blockablePages.includes(location.pathname);
  };

  return (
    <div className="min-h-screen bg-canvas flex">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-3 left-3 z-50">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-xl border border-gray-200 bg-white text-ink shadow-sm hover:bg-gray-50"
          aria-label="Open menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <main className="bg-canvas pb-20 lg:pb-0">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* Feedback Widget */}
      <FeedbackWidget />

      {/* Blocking Modals */}
      {shouldShowBlocking() && !isTransport && (
        <>
          {/* Overdue Reports - Now closable */}
          <BlockingModal
            isOpen={showOverdueModal}
            type="overdue_report"
            jobs={overdueReportJobs}
            onClose={() => setShowOverdueModal(false)}
          />
        </>
      )}
    </div>
  );
};

export default AuthenticatedLayout;
